import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { completeCheckout } from "../../actions/billing";
import { openHelcimCheckout } from "../../utils/helcim";
import { openPaddleSandboxCheckout } from "../../utils/paddleSandbox";

// How long the backend asks for when it answers "processing".
const SETTLE_WAIT_MS = 3000;

/**
 * Taking a card payment, from the three places that start one: the change-plan
 * modal, the retry button on the paused banner, and the public checkout page.
 *
 * Each of them gets a checkout from its own endpoint and then does the same
 * two things - open the popup, and hand what it reports to
 * POST /billing/checkout/complete. That is what this holds.
 *
 * `pay(checkout)` resolves once the payment is finished with, one way or the
 * other:
 *   { done: true,  status: "completed" | "processing", message }
 *   { done: false, cancelled: true }   the popup was closed without paying
 *   { done: false, message }           the payment could not be confirmed
 *
 * A declined card does not resolve: the popup stays open for another card, and
 * `error` carries what to show meanwhile.
 */
export default function useHelcimPayment() {
  const dispatch = useDispatch();
  // Closes the popup and stops listening. Held in a ref so unmounting can call
  // it - an iframe left behind would sit over whatever comes next.
  const closeRef = useRef(null);
  // "" | "card" (popup open) | "confirming" (asking the backend, and waiting
  // out the few seconds a "processing" answer needs)
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");

  useEffect(
    () => () => {
      closeRef.current?.();
      closeRef.current = null;
    },
    [],
  );

  const pay = useCallback(
    (checkout) =>
      new Promise((resolve) => {
        if (checkout?.provider === "paddle") {
          setStage("card");
          setError("");
          let settled = false;
          const finishPaddle = (value) => {
            if (settled) return;
            settled = true;
            // Paddle's success screen can stay on top of Hostor unless the
            // overlay is explicitly closed. Close it before resolving so
            // Checkout/ChangePlan can navigate or refresh the underlying page.
            const close = closeRef.current;
            closeRef.current = null;
            window.__hostorPaddleEvent = null;
            try { close?.(); } catch (err) {
              console.warn("Could not close Paddle checkout overlay", err);
            }
            setStage("");
            resolve(value);
          };
          openPaddleSandboxCheckout(checkout, (event) => {
            if (event.name === "checkout.completed") {
              // The signed Paddle webhook is authoritative for plan access.
              // This event only confirms the browser finished payment.
              finishPaddle({ done: true, status: "processing", message: "Payment submitted. Your subscription is being activated." });
            } else if (event.name === "checkout.closed") {
              finishPaddle({ done: false, cancelled: true });
            }
          }).then((close) => { if (settled) close(); else closeRef.current = close; })
            .catch((err) => finishPaddle({ done: false, message: err.message || "Unable to start Paddle checkout." }));
          return;
        }
        if (!checkout?.checkoutToken) {
          resolve({ done: false, message: "That payment has nothing to pay." });
          return;
        }

        setError("");
        setStage("card");

        const finish = (result) => {
          closeRef.current?.();
          closeRef.current = null;
          setStage("");
          resolve(result);
        };

        closeRef.current = openHelcimCheckout({
          checkoutToken: checkout.checkoutToken,

          onSuccess: async ({ data, hash }) => {
            setStage("confirming");

            const result = await dispatch(
              completeCheckout(checkout.checkoutToken, data, hash),
            );

            if (!result?.success) {
              setError(result?.message || "We couldn't confirm that payment.");
              finish({ done: false, message: result?.message || "" });
              return;
            }

            // "processing" means the payment is through but the plan is a
            // moment behind it. The backend asks for three seconds before the
            // subscription is read again, and waiting here means every caller
            // gets that for free.
            if (result.status === "processing") {
              await new Promise((resolve) => setTimeout(resolve, SETTLE_WAIT_MS));
            }

            finish({
              done: true,
              status: result.status,
              message: result.message,
            });
          },

          // The card was refused. The popup is still up, so this is a message
          // to show rather than an end to the attempt.
          onDeclined: (message) => setError(message),

          onClosed: () => {
            closeRef.current = null;
            setStage("");
            resolve({ done: false, cancelled: true });
          },
        });
      }),
    [dispatch],
  );

  return { pay, stage, error, setError };
}
